import React from "react";
import { Box, IconButton, Typography, styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  StewartDialog,
  StewartDialogActions,
} from "controls/global/dialogs/stewart-dialog/StewartDialog";
import ActionButton from "controls/global/action-button";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gradients,
  iconSize,
  padding,
  gaps,
} from "theme/defaultStyle";

import SearchIcon from "@mui/icons-material/Search";
import PreferredUnderwriterTable from "./PreferredUnderwriterTable/PreferredUnderwriterTable";

export interface UnderwriterItem {
  uniqueIdentifier: string;
  lastName: string;
  firstName: string;
  userID: string;
  email: string;
}

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxWidth: "1000px",
    minWidth: "1000px",
    maxHeight: "calc(100vh - 100px)",
    height: "auto",
    paddingTop: padding.xlarge21,
    paddingRight: padding.xlarge22,
    paddingBottom: padding.zero,
    paddingLeft: padding.xlarge22,
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.large1,
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled(Box)({
  background: gradients.blueGradient08,
  borderRadius: borderRadius.large,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const CloseIconContainer = styled(Box)({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: "auto",
  width: "32px",
  height: "32px",
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
  },
  "& .iconButton": {
    padding: padding.zero,
  },
});

const StyledDialogAction = styled(StewartDialogActions)({
  justifyContent: "right",
  padding: `${padding.large1} ${padding.zero} ${padding.xlarge22}`,
  flexShrink: 0,
});

const TableContainer = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
});

type SelectPreferredUnderwriterDialogProps = {
  schema: string;
  isOpen: boolean;
  onClose: () => void;
  preferredUnderwriters: UnderwriterItem[];
  selectedUnderwriter: UnderwriterItem | null;
  setSelectedUnderwriter: (underwriter: UnderwriterItem | null) => void;
  handleSubmit: () => void;
};

const SelectPreferredUnderwriterDialog = ({
  isOpen,
  onClose,
  preferredUnderwriters,
  selectedUnderwriter,
  setSelectedUnderwriter,
  handleSubmit,
  schema,
}: SelectPreferredUnderwriterDialogProps) => {

  return (
    <StyledDialog open={isOpen} onClose={onClose}>
      <StyledDialogTitle>
        <IconContainer>
          <SearchIcon />
        </IconContainer>
        <Typography>Select Preferred Underwriter</Typography>
        <CloseIconContainer>
          <IconButton className="iconButton" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </CloseIconContainer>
      </StyledDialogTitle>

      <TableContainer mt={4} display="flex" flexDirection="column" gap={3}>
        <PreferredUnderwriterTable
          underwriterItems={preferredUnderwriters}
          selectedUnderwriter={selectedUnderwriter}
          setSelectedUnderwriter={setSelectedUnderwriter}
          schema={schema}
        />
      </TableContainer>

      <StyledDialogAction>
        <>
          <ActionButton
            variant="outlined"
            color="secondary"
            onClick={() => onClose()}
            disabled={false}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="contained"
            color="primary"
            onClick={() => handleSubmit()}
            disabled={!selectedUnderwriter}
          >
            Select
          </ActionButton>
        </>
      </StyledDialogAction>
    </StyledDialog>
  );
};

export default SelectPreferredUnderwriterDialog;
